"use client";

import {
  Trash2,
  Settings,
  Sparkles,
  Sun,
  Moon,
  Monitor,
  Menu,
  X,
  LogOut,
  Bot,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import { useLanguage } from "../../context/LanguageContext";
import LanguageSelector from "../../i18n/components/LanguageSelector";
import OrganizationSelector from "../../i18n/components/OrganizationSelector";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  APP_LOGO,
  CURRENT_ORG,
  LOGGED_IN_USER_DATA,
  CURRENT_ORG_IDENTIFIER,
} from "src/config/constants";
import useUserStore from "../../stores/userStore";

const Header = ({ messages = [], onClearChat }) => {
  const { t, i18n } = useTranslation();
  const { theme, changeTheme, toggleTheme } = useTheme();
  const { currentLanguage, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  // No longer need isSettingsOpen since we're navigating to a separate page
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const email = localStorage.getItem("email");
    if (email) {
      setIsLoggedIn(true);
    }
    const userDataString = localStorage.getItem(LOGGED_IN_USER_DATA);
    const currentOrgString = localStorage.getItem(CURRENT_ORG);

    const userData = userDataString ? JSON.parse(userDataString) : null;
    const org = currentOrgString ? JSON.parse(currentOrgString) : null;

    if (userData) {
      console.log("Logged in user:", userData);
      setOrganizations(userData.organizations);
    }

    if (org) {
      console.log("Current organization:", org);
      setSelectedOrg(org);
    }
  }, []);

  const getThemeIcon = () => {
    switch (theme) {
      case "dark":
        return <Moon className="w-4 h-4" />;
      case "light":
        return <Sun className="w-4 h-4" />;
      default:
        return <Monitor className="w-4 h-4" />;
    }
  };

  const { logout } = useUserStore();

  const handleLogout = () => {
    // Use the userStore logout function which already clears state
    logout();

    // Clear any additional localStorage items not handled by userStore
    localStorage.removeItem("email");
    localStorage.removeItem("signupEmail");
    localStorage.removeItem("user");

    // Navigate to login page
    navigate("/login");
  };

  // Removed handleSettingsClosed as we're now navigating to a page

  return (
    <>
      <header className="sm:fixed sm:top-0 sm:left-0 sm:right-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-700/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-1 sm:px-6 py-1.5 sm:py-3 flex items-center justify-between">
          <div className="flex items-center gap-1 sm:gap-3">
            <div className="relative">
              {/* <div className="w-7 h-7 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div> */}
              <div className="flex items-center justify-center">
                <img
                  src={APP_LOGO}
                  alt="SOCBuddy Logo"
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                />
              </div>
              {/* <div className="absolute -top-0.5 -right-0.5 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full border-2 border-white animate-pulse"></div> */}
            </div>
            <div>
              <h1 className="text-xs sm:text-lg font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                {t("common:poweredBy")}
              </h1>
              <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <Sparkles className="w-2 h-2 sm:w-2.5 sm:h-2.5" />
                <span className="hidden sm:inline">
                  {t("common:cyberGuard", "Jarvis of cyber security")}
                </span>
                <span className="sm:hidden">
                  {t("common:cyberGuard", "Jarvis of cyber security")}
                </span>
              </p>
            </div>
          </div>
          {/* Hamburger menu for mobile */}
          <div className="flex items-center sm:hidden">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 focus:outline-none"
              title="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
          {/* Inline menu for desktop */}
          <div className="hidden sm:flex items-center gap-2">
            {/* Agent Button */}
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-1 px-2.5 py-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50/80 dark:hover:bg-blue-900/30 rounded-lg transition-all duration-300 backdrop-blur-sm"
              title={t("common:agent", "Agent")}
            >
              <Bot className="w-4 h-4" />
              <span className="hidden md:inline text-sm">
                {t("common:agent", "Agent")}
              </span>
            </button>

            {/* Language Dropdown */}
            <LanguageSelector isMobile={false} />

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="flex items-center gap-1 px-2.5 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-lg transition-all duration-300 backdrop-blur-sm"
              title={t("common:theme")}
            >
              {getThemeIcon()}
              <span className="hidden md:inline text-sm">
                {theme === "dark"
                  ? t("common:dark")
                  : theme === "light"
                  ? t("common:light")
                  : t("common:system")}
              </span>
            </button>
            {/* Settings Button */}
            <button
              onClick={() => {
                const email = localStorage.getItem("email");
                if (!email) {
                  navigate("/verify-email");
                } else {
                  navigate("/settings");
                }
              }}
              className="flex items-center gap-1 px-2.5 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-lg transition-all duration-300 backdrop-blur-sm"
              title={t("common:settings")}
            >
              <Settings className="w-4 h-4" />
              <span className="hidden md:inline text-sm">
                {t("common:settings")}
              </span>
            </button>
            {/* Clear Chat Button - icon only, if there are messages */}
            {messages && messages.length > 0 && (
              <button
                onClick={onClearChat}
                className="flex items-center gap-1 px-2.5 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 bg-transparent hover:bg-gray-100/60 dark:hover:bg-gray-800/60 rounded-lg transition-all duration-200"
                title={t("common:clearChat")}
                type="button"
              >
                <Trash2 className="w-5 h-5" />
                <span>{t("common:clearChat")}</span>
              </button>
            )}

            {isLoggedIn && organizations && organizations.length > 0 && (
              <div className="flex items-center gap-2 px-2 py-2">
                <OrganizationSelector organizations={organizations} />
              </div>
            )}

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-1 px-2.5 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50/80 dark:hover:bg-red-900/30 rounded-lg transition-all duration-300"
              title={t("common:logout", "Logout")}
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline text-sm">
                {t("common:logout", "Logout")}
              </span>
            </button>
          </div>
        </div>
      </header>
      {/* Sidebar for mobile */}
      {sidebarOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity z-50"
            onClick={() => setSidebarOpen(false)}
          />
          {/* Sidebar */}
          <div className="fixed left-0 top-0 bottom-0 z-50 w-72 max-w-full bg-white dark:bg-gray-900 shadow-2xl h-full flex flex-col p-6 animate-in slide-in-from-left-4 duration-300">
            <div className="flex items-center justify-between mb-8">
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center mb-2">
                  <img
                    src={APP_LOGO}
                    alt="SOCBuddy Logo"
                    className="w-16 h-16 object-contain"
                  />
                </div>
                <span className="text-sm font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent text-center">
                  {t("common:cyberGuard", "Jarvis of cyber security")}
                </span>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-gray-500 hover:text-gray-900 dark:hover:text-white rounded-lg transition-colors absolute top-4 right-4"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex flex-col gap-4">
              {/* Agent Button (Mobile) */}
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  navigate("/");
                }}
                className="flex items-center gap-2 px-2 py-2 text-base text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all duration-200"
                title={t("common:agent", "Agent")}
              >
                <Bot className="w-5 h-5" />
                {t("common:agent", "Agent")}
              </button>

              <button
                onClick={toggleTheme}
                className="flex items-center gap-2 px-2 py-2 text-base text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-lg transition-all duration-300 backdrop-blur-sm"
                title={t("common:theme")}
              >
                {getThemeIcon()}
                <span>
                  {theme === "dark"
                    ? t("common:dark")
                    : theme === "light"
                    ? t("common:light")
                    : t("common:system")}
                </span>
              </button>
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  const email = localStorage.getItem("email");
                  if (!email) {
                    navigate("/verify-email");
                  } else {
                    navigate("/settings");
                  }
                }}
                className="flex items-center gap-2 px-2 py-2 text-base text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100/80 dark:hover:bg-gray-800/80 rounded-lg transition-all duration-300 backdrop-blur-sm"
                title={t("common:settings")}
              >
                <Settings className="w-5 h-5" />
                <span>{t("common:settings")}</span>
              </button>
              <div className="mb-2">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 px-2">
                  {t("common:language")}
                </p>
                <div className="flex flex-wrap gap-2 px-2">
                  {[
                    { code: "en", name: "EN", flag: "🇺🇸" },
                    { code: "ar", name: "AR", flag: "🇸🇦" },
                    { code: "de", name: "DE", flag: "🇩🇪" },
                    { code: "ja", name: "JP", flag: "🇯🇵" },
                    { code: "tr", name: "TR", flag: "🇹🇷" },
                  ].map((language) => (
                    <button
                      key={language.code}
                      onClick={() => i18n.changeLanguage(language.code)}
                      className={`flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-sm 
                      ${
                        currentLanguage === language.code
                          ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70"
                      }`}
                    >
                      <span>{language.flag}</span>
                      <span className="font-medium">{language.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {isLoggedIn && organizations && organizations.length > 0 && (
                <div className="mb-2">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 px-2">
                    {t("common:organization", "Organization")}
                  </p>
                  <div className="flex flex-wrap gap-2 px-2">
                    {organizations.map((org) => (
                      <button
                        key={org.identifier}
                        onClick={() => {
                          localStorage.setItem(
                            CURRENT_ORG,
                            JSON.stringify(org)
                          );
                          localStorage.setItem(
                            "CURRENT_ORG_IDENTIFIER",
                            org.identifier
                          );
                          setSelectedOrg(org);
                        }}
                        className={`flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-sm 
                        ${
                          selectedOrg?.identifier === org.identifier
                            ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/70"
                        }`}
                      >
                        <span className="font-medium">{org.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages && messages.length > 0 && (
                <button
                  onClick={() => {
                    setSidebarOpen(false);
                    onClearChat();
                  }}
                  className="flex items-center gap-2 px-2 py-2 text-base font-medium text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 bg-transparent hover:bg-gray-100/60 dark:hover:bg-gray-800/60 rounded-lg transition-all duration-200"
                  title={t("common:clearChat")}
                  type="button"
                >
                  <Trash2 className="w-5 h-5" />
                  {t("common:clearChat")}
                </button>
              )}

              {/* Logout Button (Mobile) */}
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  handleLogout();
                }}
                className="flex items-center gap-2 px-2 py-2 text-base text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200"
                title={t("common:logout", "Logout")}
              >
                <LogOut className="w-5 h-5" />
                {t("common:logout", "Logout")}
              </button>
            </nav>
          </div>
        </>
      )}
      {/* Settings modal removed - now using a separate page */}
    </>
  );
};

export default Header;
